import { assert, assertNever } from '~/util';
import { evaluateInt, evaluateNum, evaluateNumArray } from './parameter';
import { wfBlackman } from './waveform/blackman';
import { wfConstant } from './waveform/constant';
import { wfCustom } from './waveform/custom';
import { wfInterpolated } from './waveform/interpolated';
import { wfKaiser } from './waveform/kaiser';
import { wfRamp } from './waveform/ramp';
import type * as schema from '~/models/pulser-schema';
import type { Constraint, Waveform } from '~/models/studio/waveform';
import type { Bounds, Vec2 } from '~/util';

export const DELTA_T = 0.001;

export interface WaveformApi<Wf> {
  samples: (wf: Wf) => number[];
  sampleAt: (wf: Wf, t: number) => number;
  area: (wf: Wf) => number;
  bounds: (wf: Wf) => Bounds;
  points: (wf: Wf, xOffset: number) => Vec2[];
  duration: (wf: Wf) => number;
}

/** Default implementation of waveform `sampleAt`, evaluating all samples */
export function defaultSampleAt<T>(
  this: WaveformApi<T>,
  wf: T,
  t: number
): number {
  return this.samples(wf)[Math.floor(t)];
}

/** waveform duration in ns */
export function waveformDuration(wf: Waveform): number {
  switch (wf.kind) {
    case 'constant':
      return wfConstant.duration(wf);
    case 'ramp':
      return wfRamp.duration(wf);
    case 'blackman':
      return wfBlackman.duration(wf);
    case 'kaiser':
      return wfKaiser.duration(wf);
    case 'interpolated':
      return wfInterpolated.duration(wf);
    case 'custom':
      return wfCustom.duration(wf);
    default:
      assertNever(wf);
  }
}

/** Get the definite integral of the waveform over its whole duration. */
export function waveformArea(wf: Waveform): number {
  switch (wf.kind) {
    case 'constant':
      return wfConstant.area(wf);
    case 'ramp':
      return wfRamp.area(wf);
    case 'blackman':
      return wfBlackman.area(wf);
    case 'kaiser':
      return wfKaiser.area(wf);
    case 'interpolated':
      return wfInterpolated.area(wf);
    case 'custom':
      return wfCustom.area(wf);
    default:
      assertNever(wf);
  }
}

/** Sample waveform value at specfied time */
export function waveformSampleAt(wf: Waveform, t: number): number {
  switch (wf.kind) {
    case 'constant':
      return wfConstant.sampleAt(wf, t);
    case 'ramp':
      return wfRamp.sampleAt(wf, t);
    case 'blackman':
      return wfBlackman.sampleAt(wf, t);
    case 'kaiser':
      return wfKaiser.sampleAt(wf, t);
    case 'interpolated':
      return wfInterpolated.sampleAt(wf, t);
    case 'custom':
      return wfCustom.sampleAt(wf, t);
    default:
      assertNever(wf);
  }
}

/** simplified waveform shape points, useful for rendering */
export function waveformPoints(wf: Waveform, xOffset: number): Vec2[] {
  switch (wf.kind) {
    case 'constant':
      return wfConstant.points(wf, xOffset);
    case 'blackman':
      return wfBlackman.points(wf, xOffset);
    case 'kaiser':
      return wfKaiser.points(wf, xOffset);
    case 'ramp':
      return wfRamp.points(wf, xOffset);
    case 'interpolated':
      return wfInterpolated.points(wf, xOffset);
    case 'custom':
      return wfCustom.points(wf, xOffset);
    default:
      return assertNever(wf);
  }
}

/** precise waveform sample values, one sample per ns. Used for simulation. */
export function waveformSamples(wf: Waveform): number[] {
  switch (wf.kind) {
    case 'constant':
      return wfConstant.samples(wf);
    case 'ramp':
      return wfRamp.samples(wf);
    case 'blackman':
      return wfBlackman.samples(wf);
    case 'kaiser':
      return wfKaiser.samples(wf);
    case 'interpolated':
      return wfInterpolated.samples(wf);
    case 'custom':
      return wfCustom.samples(wf);
    default:
      return assertNever(wf);
  }
}

/** approximation min and max sample value, used for rendering only */
export function waveformBounds(wf: Waveform): Bounds {
  switch (wf.kind) {
    case 'constant':
      return wfConstant.bounds(wf);
    case 'ramp':
      return wfRamp.bounds(wf);
    case 'blackman':
      return wfBlackman.bounds(wf);
    case 'kaiser':
      return wfKaiser.bounds(wf);
    case 'interpolated':
      return wfInterpolated.bounds(wf);
    case 'custom':
      return wfCustom.bounds(wf);
    default:
      return assertNever(wf);
  }
}

/**
 * Create an approximately equivalent waveform of chosen kind from another waveform.
 * Used to generate initial waveform data after the user has edited the waveform kind.
 *
 * This equivalence doesn't have to be perfect, but in general it should try to preserve
 * the duration and area of the source waveform.
 */
export function changeWaveformKind(
  wf: Waveform,
  newKind: Waveform['kind']
): Waveform {
  const constraint: Constraint =
    'constraint' in wf
      ? wf.constraint
      : {
          kind: 'maxVal',
          duration: waveformDuration(wf),
          area: waveformArea(wf)
        };

  switch (newKind) {
    case 'constant':
    case 'blackman':
      return {
        kind: newKind,
        constraint
      };
    case 'kaiser': {
      return {
        kind: 'kaiser',
        constraint,
        beta: 14
      };
    }
    case 'ramp': {
      const duration = waveformDuration(wf);
      const area = waveformArea(wf);
      return {
        kind: 'ramp',
        duration: waveformDuration(wf),
        start: 0,
        stop: (area * 2) / (duration * DELTA_T)
      };
    }
    case 'interpolated': {
      const duration = waveformDuration(wf);
      const area = waveformArea(wf);
      return {
        kind: 'interpolated',
        times: [0, 0.5, 1],
        values: [0, (area * 2) / (duration * DELTA_T), 0],
        duration
      };
    }
    case 'custom': {
      const samples = waveformSamples(wf);
      return { kind: 'custom', samples };
    }
    default:
      assertNever(newKind);
  }
}

/** Get total duration of series of waveforms */
export function waveformsDuration(waveforms: readonly Waveform[]): number {
  return waveforms.reduce(
    (duration, waveform) => duration + waveformDuration(waveform),
    0
  );
}

/** Find waveform at specified time for given series of waveforms */
export function findWaveformAt(
  wfs: readonly Waveform[],
  t: number
): { waveform: Waveform; t: number } {
  assert(wfs.length > 0);
  let duration: number | null = null;
  for (const wf of wfs) {
    duration = waveformDuration(wf);
    if (t < duration) {
      return { waveform: wf, t };
    } else {
      t -= duration;
    }
  }
  assert(duration != null);
  // if we get here, t is greater than the duration of all waveforms.
  // That is interpreted as using last sample of last waveform.
  return { waveform: wfs[wfs.length - 1], t: duration };
}

/**
 * Get the simplified outline points for given series of waveforms.
 * Those points are only used for rendering purposes, and thus can be
 * simplified or approximated.
 *
 * The points are returned as an array of Vec2, with the first point
 * being the start of the first waveform, and the last point being the
 * end of the last waveform. The points are all drawn as connected with
 * a straight line.
 */
export function waveformsPoints(
  wfs: readonly Waveform[] | null,
  duration: number | null = null
): Vec2[][] {
  if (wfs == null) {
    return [];
  }

  let offset = 0;
  const wfsPoints = wfs
    .map((wf) => {
      const points = waveformPoints(wf, offset);
      offset += waveformDuration(wf);
      return points;
    })
    .filter((points) => points.length > 0);

  const constantZeroWfDuration = duration !== null ? duration - offset : 0;
  if (constantZeroWfDuration > 0) {
    //add a constant zero waveform at the end
    const constantZeroWf: Waveform = {
      kind: 'constant',
      constraint: {
        kind: 'maxVal',
        area: 0,
        duration: constantZeroWfDuration
      }
    };
    wfsPoints.push(waveformPoints(constantZeroWf, offset));
  }
  return wfsPoints;
}

/**
 * Import common serialization format Waveform into internal waveform representation.
 *
 * Receives a list of variables defined in Pulser, and bakes their result into the
 * internal structure by evaluating them at default values.
 * */
export function importWaveform(
  wf: schema.Waveform,
  vars: Record<string, schema.Variable>
): Waveform[] {
  switch (wf.kind) {
    case 'composite':
      return wf.waveforms.flatMap((c) => importWaveform(c, vars));
    case 'constant':
      return [
        {
          kind: 'constant',
          constraint: {
            kind: 'area',
            duration: evaluateInt(wf.duration, vars),
            maxVal: evaluateNum(wf.value, vars)
          }
        }
      ];
    case 'ramp':
      return [
        {
          kind: 'ramp',
          duration: evaluateInt(wf.duration, vars),
          start: evaluateNum(wf.start, vars),
          stop: evaluateNum(wf.stop, vars)
        }
      ];
    case 'blackman':
      return [
        {
          kind: 'blackman',
          constraint: {
            kind: 'maxVal',
            duration: evaluateInt(wf.duration, vars),
            area: evaluateNum(wf.area, vars)
          }
        }
      ];
    case 'blackman_max':
      return [
        {
          kind: 'blackman',
          constraint: {
            kind: 'duration',
            maxVal: evaluateInt(wf.max_val, vars),
            area: evaluateNum(wf.area, vars)
          }
        }
      ];
    case 'interpolated':
      return [
        {
          kind: 'interpolated',
          duration: evaluateInt(wf.duration, vars),
          values: evaluateNumArray(wf.values, vars),
          times: evaluateNumArray(wf.times, vars)
        }
      ];
    case 'kaiser':
      return [
        {
          kind: 'kaiser',
          constraint: {
            kind: 'maxVal',
            duration: evaluateInt(wf.duration, vars),
            area: evaluateNum(wf.area, vars)
          },
          beta: evaluateNum(wf.beta, vars)
        }
      ];

    case 'kaiser_max':
      return [
        {
          kind: 'kaiser',
          constraint: {
            kind: 'duration',
            maxVal: evaluateInt(wf.max_val, vars),
            area: evaluateNum(wf.area, vars)
          },
          beta: evaluateNum(wf.beta, vars)
        }
      ];
    case 'custom':
      return [
        {
          kind: 'custom',
          samples: evaluateNumArray(wf.samples, vars)
        }
      ];
    default:
      return assertNever(wf);
  }
}

/** Export internal waveform model into common Pulser serialization format */
export function exportWaveform(wf: Waveform): schema.Waveform {
  switch (wf.kind) {
    case 'constant':
      return {
        kind: 'constant',
        duration: waveformDuration(wf),
        value: waveformBounds(wf).max
      };
    case 'ramp':
      return wf;
    case 'blackman':
      switch (wf.constraint.kind) {
        case 'maxVal':
          return {
            kind: 'blackman',
            area: wf.constraint.area,
            duration: wf.constraint.duration
          };
        case 'duration':
          return {
            kind: 'blackman_max',
            area: wf.constraint.area,
            max_val: wf.constraint.maxVal
          };
        case 'area':
          return {
            kind: 'blackman_max',
            area: waveformArea(wf),
            max_val: wf.constraint.maxVal
          };
        default:
          return assertNever(wf.constraint);
      }
    case 'kaiser':
      switch (wf.constraint.kind) {
        case 'maxVal':
          return {
            kind: 'kaiser',
            area: wf.constraint.area,
            beta: wf.beta,
            duration: wf.constraint.duration
          };
        case 'duration':
          return {
            kind: 'kaiser_max',
            area: wf.constraint.area,
            beta: wf.beta,
            max_val: wf.constraint.maxVal
          };
        case 'area':
          return {
            kind: 'blackman_max',
            area: waveformArea(wf),
            max_val: wf.constraint.maxVal
          };
        default:
          return assertNever(wf.constraint);
      }
    case 'interpolated':
      return {
        kind: 'interpolated',
        duration: wf.duration,
        times: [...wf.times],
        values: [...wf.values]
      };
    case 'custom':
      return { kind: 'custom', samples: [...wf.samples] };
    default:
      assertNever(wf);
  }
}
